import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { PawPrints } from './PawPrints'
import { Terrier } from './Terrier'
import { POSE_HEIGHT, SITTING_SIDE, STANDING } from './terrierArt'
import { VISIT_MS, prefersReducedMotion } from './timing'

/**
 * Fractions of the visit at which he arrives and sets off again.
 *
 * These have to agree with the terrier-visit keyframes, which hold the same
 * transform between them - the pose swaps here, the travel happens there, and if
 * the two disagreed he would sit down mid-stride.
 */
const ARRIVES_AT = 0.26
const LEAVES_AT = 0.74

/**
 * How long after the retriever sets off before the terrier comes on behind him.
 *
 * The retriever takes WANDER_MS to cross, so this is a fraction of the way into
 * his trip: long enough that the terrier is clearly trailing him rather than
 * abreast of him, short enough that they are on screen together.
 */
const FOLLOW_DELAY_MS = 2800

/**
 * How often he bothers, after the first time.
 *
 * Not every time, or the pair of them become a procession on a timer rather than
 * something you happen to catch. The first retriever cameo always brings him out
 * though - a surprise nobody ever sees is just dead code.
 */
const CHANCE = 0.5

/** Smaller than the retriever - he is a terrier. */
const HEIGHT_PX = 74

/** Keeps his prints off the very edge of the screen, as the retriever's are. */
const GROUND_INSET = 6

type Stage = 'arriving' | 'sitting' | 'leaving'

interface WanderingTerrierProps {
  /**
   * Changes each time the retriever sets off, carrying the way he is going. The
   * terrier comes on behind him, heading the same way.
   */
  cue: { id: number; heading: 'left' | 'right' } | null
}

/**
 * The terrier trots on after the retriever, sits down for a bit, then goes back
 * out the way he came.
 *
 * He follows the retriever rather than keeping his own schedule, which is the
 * whole joke - the big dog wanders past on business of his own, and a little one
 * comes after him, thinks better of it, and sits down.
 *
 * The travel is a single CSS animation for the round trip so his position cannot
 * drift between the two legs. What lives here is the pose: standing while he
 * moves, sitting while he does not, and which way he is facing - he turns round
 * to leave, because the artwork faces one way and he does not walk backwards.
 */
export function WanderingTerrier({ cue }: WanderingTerrierProps) {
  const [visit, setVisit] = useState<{ id: number; heading: 'left' | 'right' } | null>(null)
  const [stage, setStage] = useState<Stage>('arriving')
  const comeOutBefore = useRef(false)
  const outNow = useRef(false)
  const timers = useRef<number[]>([])

  // Only on the way out. Deliberately not tied to the cue: cancelling a visit
  // because the retriever set off again would cut the terrier off mid-stride, and
  // the more often the retriever goes, the more often that would happen.
  useEffect(() => () => timers.current.forEach(window.clearTimeout), [])

  useEffect(() => {
    if (cue === null || prefersReducedMotion()) return
    // Already out. A dog cannot come on twice at once, and the retriever now
    // goes often enough that his next trip can start before this one is over.
    if (outNow.current) return
    if (comeOutBefore.current && Math.random() > CHANCE) return
    comeOutBefore.current = true
    outNow.current = true

    const later = (fn: () => void, ms: number) => timers.current.push(window.setTimeout(fn, ms))

    later(() => {
      setStage('arriving')
      setVisit(cue)

      later(() => setStage('sitting'), VISIT_MS * ARRIVES_AT)
      later(() => setStage('leaving'), VISIT_MS * LEAVES_AT)
      later(() => {
        setVisit(null)
        outNow.current = false
        timers.current = []
      }, VISIT_MS)
    }, FOLLOW_DELAY_MS)
  }, [cue])

  if (visit === null) return null

  const pose = stage === 'sitting' ? SITTING_SIDE : STANDING
  const scale = HEIGHT_PX / POSE_HEIGHT
  /*
   * Everything is positioned by his nose rather than by the box around him.
   *
   * The poses are different widths, so placing them by their left edge moved him
   * sideways the moment he sat - 54px of it, which read as a flinch. Working from
   * the anchor keeps his head still and lets his back end tuck under, and it
   * means the travel coordinates do not depend on which pose he is in.
   */
  const anchorPx = pose.anchorX * scale

  // He walks in the cued direction, so he came in from the far side of it.
  const goingLeft = visit.heading === 'left'
  const from = goingLeft ? window.innerWidth + 80 : -80
  // Far enough in to be worth looking at, not so far that he is centre stage.
  const stop = window.innerWidth * (goingLeft ? 0.62 : 0.38)

  // The artwork faces right, so heading left is the mirror - and he turns round
  // when he sets off home.
  const facingLeft = stage === 'leaving' ? !goingLeft : goingLeft
  // Mirroring about the anchor rather than the box: scale first, which sends the
  // anchor to -anchorPx, then bring it back to the origin.
  const orient = facingLeft
    ? `translateX(${anchorPx}px) scaleX(-1)`
    : `translateX(${-anchorPx}px)`
  // His paws are a little behind his nose, so the trail is too.
  const pawOffset = goingLeft ? 26 : -26

  return (
    <>
      <div className="absolute" style={{ bottom: GROUND_INSET }}>
        {/* One trail on the way in, one on the way out, each timed to its own
            leg of the single travel animation. */}
        <PawPrints
          key={`in-${visit.id}`}
          from={from + pawOffset}
          to={stop + pawOffset}
          travelMs={VISIT_MS}
          endAt={ARRIVES_AT}
          count={7}
          lifeMs={2600}
        />
        <PawPrints
          key={`out-${visit.id}`}
          from={stop + pawOffset}
          to={from + pawOffset}
          travelMs={VISIT_MS}
          startAt={LEAVES_AT}
          count={7}
          lifeMs={2600}
        />
      </div>

      <div
        className="animate-terrier-visit absolute"
        style={
          {
            bottom: 0,
            '--terrier-from': `${from}px`,
            '--terrier-stop': `${stop}px`,
            '--visit-duration': `${VISIT_MS}ms`,
          } as CSSProperties
        }
      >
        {/* Only bobs while he is on the move. */}
        <div className={stage === 'sitting' ? undefined : 'animate-dog-bob'}>
          {/* transform-origin must be the left edge, not the default centre.
              Mirroring about the centre reflects the anchor to the far side of a
              box whose width changes with the pose, which put a 45px sidestep in
              the sit every time he was heading left. */}
          <div style={{ transform: orient, transformOrigin: '0 center' }}>
            <Terrier pose={pose} heightPx={HEIGHT_PX} className="opacity-90 drop-shadow-sm" />
          </div>
        </div>
      </div>
    </>
  )
}
